import type { ClientProgramTarget } from '@/api/clientProgramTargets';
import type { ChainingMethod } from '@/api/programBank';
import { FrequencyCounter } from './FrequencyCounter';
import { DurationStopwatch } from './DurationStopwatch';
import { TrialGrid } from './TrialGrid';
import { PercentEntry } from './PercentEntry';
import { TaskAnalysisStepper } from './TaskAnalysisStepper';
import type { EntryState } from './types';

/*
 * TargetDataEntry — dispatcher that picks the right widget based on the
 * target's data_type. Keeps DataCollectionPane free of conditional
 * rendering noise.
 *
 * Each widget owns its own input affordances; this layer only adapts
 * the target's stored config (trials count, ta_steps, chaining_method)
 * into the shape each widget needs.
 */
interface TargetDataEntryProps {
    target: ClientProgramTarget;
    value: EntryState;
    onChange: (next: EntryState) => void;
    disabled?: boolean;
}

export function TargetDataEntry({ target, value, onChange, disabled }: TargetDataEntryProps) {
    switch (target.dataType) {
        case 'trial':
            return (
                <TrialGrid
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    trialsCount={target.trials ?? 0}
                />
            );
        case 'percent':
            return <PercentEntry value={value} onChange={onChange} disabled={disabled} />;
        case 'frequency':
            return <FrequencyCounter value={value} onChange={onChange} disabled={disabled} />;
        case 'duration':
            return <DurationStopwatch value={value} onChange={onChange} disabled={disabled} />;
        case 'task_analysis':
            return (
                <TaskAnalysisStepper
                    value={value}
                    onChange={onChange}
                    disabled={disabled}
                    steps={(target.taSteps as Array<{ label?: string }>) ?? []}
                    chainingMethod={target.chainingMethod as ChainingMethod | null}
                />
            );
        default:
            return <p className="text-xs text-text-subtle">Unsupported data type: {target.dataType}</p>;
    }
}
